/**
 * Service Category Mapping
 * Maps WordPress service categories to the new 4 main categories structure
 */

export interface ServiceCategory {
  id: string;
  name: string;
  nameAr: string;
  subCategories: SubCategory[];
}

export interface SubCategory {
  id: string;
  name: string;
  nameAr: string;
  wordPressCategoryNames: string[]; // WordPress category names that map to this sub-category
  serviceCount: number;
}

// Main Categories Structure
export const serviceCategories: ServiceCategory[] = [
  {
    id: 'cybersecurity',
    name: 'Cybersecurity Services',
    nameAr: 'خدمات الأمن السيبراني',
    subCategories: [
      {
        id: 'offensive-security',
        name: 'Offensive Security',
        nameAr: 'الأمن الهجومي',
        wordPressCategoryNames: ['Offensive Security', 'offensive-security', 'Offensive Security Services'],
        serviceCount: 6
      },
      {
        id: 'defensive-security',
        name: 'Defensive Security',
        nameAr: 'الأمن الدفاعي',
        wordPressCategoryNames: ['Defensive Security', 'defensive-security', 'Defensive Security Services'],
        serviceCount: 4
      },
      {
        id: 'compliance-governance',
        name: 'Compliance & Governance',
        nameAr: 'الامتثال والحوكمة',
        wordPressCategoryNames: ['Compliance & Governance', 'compliance-governance', 'Compliance and Governance', 'Compliance & Governance Services'],
        serviceCount: 4
      },
      {
        id: 'training-capacity',
        name: 'Training & Capacity Building',
        nameAr: 'التدريب وبناء القدرات',
        wordPressCategoryNames: ['Training & Capacity Building', 'training-capacity-building', 'Training and Capacity Building', 'Training & Capacity Building Services'],
        serviceCount: 3
      }
    ]
  },
  {
    id: 'platform-development',
    name: 'Platform & Application Development',
    nameAr: 'تطوير المنصات والتطبيقات',
    subCategories: [
      {
        id: 'platform-architecture',
        name: 'Platform Architecture & Development',
        nameAr: 'هندسة المنصات والتطوير',
        wordPressCategoryNames: ['Platform Architecture & Development', 'platform-architecture-development', 'Platform Architecture and Development', 'Platform Architecture & Development Services'],
        serviceCount: 8
      },
      {
        id: 'user-experience',
        name: 'User Experience & Interface',
        nameAr: 'تجربة المستخدم والواجهة',
        wordPressCategoryNames: ['User Experience & Interface', 'user-experience-interface', 'User Experience and Interface', 'User Experience & Interface Services'],
        serviceCount: 4
      }
    ]
  },
  {
    id: 'infrastructure-devops',
    name: 'Infrastructure & DevOps',
    nameAr: 'البنية التحتية وDevOps',
    subCategories: [
      {
        id: 'infrastructure-devops',
        name: 'Infrastructure & DevOps',
        nameAr: 'البنية التحتية وDevOps',
        wordPressCategoryNames: ['Infrastructure & DevOps', 'infrastructure-devops', 'Infrastructure and DevOps', 'Infrastructure & DevOps Services'],
        serviceCount: 4
      }
    ]
  },
  {
    id: 'analytics-intelligence',
    name: 'Analytics & Intelligence',
    nameAr: 'التحليلات والذكاء',
    subCategories: [
      {
        id: 'analytics-intelligence',
        name: 'Analytics & Intelligence',
        nameAr: 'التحليلات والذكاء',
        wordPressCategoryNames: ['Analytics & Intelligence', 'analytics-intelligence', 'Analytics and Intelligence', 'Analytics & Intelligence Services'],
        serviceCount: 3
      }
    ]
  }
];

/**
 * Decode HTML entities in category name (works in both browser and Node.js)
 */
function decodeHtmlEntities(text: string): string {
  if (typeof document !== 'undefined') {
    // Browser context
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value || text;
  } else {
    // Node.js context - simple replacement for common entities
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }
}

/**
 * Normalize category name for comparison (decode HTML entities, trim, lowercase)
 */
function normalizeCategoryName(name: string): string {
  return decodeHtmlEntities(name).trim().toLowerCase();
}

/**
 * Get main category by sub-category WordPress name
 */
export function getMainCategoryByWordPressCategory(wpCategoryName: string): ServiceCategory | null {
  const normalized = normalizeCategoryName(wpCategoryName);
  for (const mainCategory of serviceCategories) {
    for (const subCategory of mainCategory.subCategories) {
      if (subCategory.wordPressCategoryNames.some(name => 
        normalizeCategoryName(name) === normalized
      )) {
        return mainCategory;
      }
    }
  }
  return null;
}

/**
 * Get sub-category by WordPress category name
 */
export function getSubCategoryByWordPressCategory(wpCategoryName: string): SubCategory | null {
  const normalized = normalizeCategoryName(wpCategoryName);
  for (const mainCategory of serviceCategories) {
    for (const subCategory of mainCategory.subCategories) {
      if (subCategory.wordPressCategoryNames.some(name => 
        normalizeCategoryName(name) === normalized
      )) {
        return subCategory;
      }
    }
  }
  return null;
}

/**
 * Get all main categories
 */
export function getAllMainCategories(): ServiceCategory[] {
  return serviceCategories;
}

/**
 * Get sub-categories for a main category
 */
export function getSubCategoriesForMainCategory(mainCategoryId: string): SubCategory[] {
  const mainCategory = serviceCategories.find(cat => cat.id === mainCategoryId);
  return mainCategory ? mainCategory.subCategories : [];
}

